import { Inject, LOCALE_ID, Pipe, PipeTransform } from '@angular/core';
import { abbreviateNumber, AbbreviationUnit } from './_abbreviate-number';

const COUNT_THRESHOLDS = {
  clients: 100_000,
  general: 10_000,
} as const;

export type SgEntityCountKind = keyof typeof COUNT_THRESHOLDS;

/**
 * Formats an **integer count of discrete entities** (clients, lenders, account holders,
 * loans, securities, contracts, trades, …) with threshold-based abbreviation per the
 * Lender Platform Number Formatting Standard v1.2 §9.
 *
 * For **units of something** (5.3 shares of Apple, 1,238,472.45 shares of MSFT), use
 * `SgUnitsCountPipe` (`sgUnitsCount`) instead — that one allows decimals and is mode-driven
 * rather than threshold-driven.
 *
 * ## Threshold by kind
 *
 * | `kind`       | Threshold | Below threshold              | Above threshold |
 * |--------------|-----------|------------------------------|-----------------|
 * | `'clients'`  | 100,000   | full + thousands (`35,450`)  | `124K`, `1.2M`  |
 * | `'general'`  | 10,000    | full + thousands (`5,847`)   | `12.4K`, `1.2M` |
 *
 * The spec rationale: every individual person (client, lender, account holder) is
 * significant business context up to 100K — abbreviating `45,324 → 45.3K` erases the
 * texture business owners care about ("we just crossed 50K", "we lost 200 last quarter").
 * Non-person entity counts (loans, securities, …) don't carry that signal so they
 * abbreviate sooner.
 *
 * `'general'` is the default — pass `'clients'` explicitly when the count is people.
 *
 * ## Special values
 *
 * - `null` / `undefined` / `NaN` / `±Infinity` → `—` (em dash, U+2014).
 * - Negative counts use U+2212 minus (`−150K`), not ASCII hyphen.
 *
 * ## Locale
 *
 * Thousands separator is locale-aware (`35,450` for en-US, `35.450` for de-DE,
 * `35 450` for fr-FR). Decimal in the abbreviated form follows the same locale.
 *
 * ## Usage
 *
 * Template:
 * ```html
 * <!-- People counts — pass 'clients' explicitly (100K threshold) -->
 * {{ vm.portfolioOverview.totalClientsNumber \| sgEntityCount : 'clients' }}
 *
 * <!-- Everything else — default 'general' kind (10K threshold) -->
 * {{ row.securitiesCount \| sgEntityCount }}
 * {{ summary.activeLoans \| sgEntityCount }}
 * ```
 *
 * Static (for non-template callers):
 * ```ts
 * SgEntityCountPipe.transform(35450, 'clients');  // '35,450'
 * SgEntityCountPipe.transform(124000, 'clients'); // '124K'
 * SgEntityCountPipe.transform(12400);             // '12.4K'  (default kind = 'general')
 * SgEntityCountPipe.transform(null);              // '—'
 * ```
 */
@Pipe({
  name: 'sgEntityCount',
  standalone: false,
})
export class SgEntityCountPipe implements PipeTransform {
  constructor(@Inject(LOCALE_ID) private readonly locale: string) {}

  transform(value: number | null | undefined, kind: SgEntityCountKind = 'general'): string {
    return SgEntityCountPipe.transform(value, kind, this.locale);
  }

  /**
   * Static variant — same behavior, no DI required.
   *
   * @param value   Integer count. Non-finite → `'—'`.
   * @param kind    `'clients'` (100K threshold) or `'general'` (10K threshold). Defaults to `'general'`.
   * @param locale  BCP-47 locale code. Defaults to `'en-US'`.
   *
   * @example
   * SgEntityCountPipe.transform(35450, 'clients');  // '35,450'
   * SgEntityCountPipe.transform(99999, 'clients');  // '99,999'  (one below clients threshold)
   * SgEntityCountPipe.transform(100000, 'clients'); // '100K'    (at clients threshold)
   * SgEntityCountPipe.transform(1234567, 'clients');// '1.2M'
   * SgEntityCountPipe.transform(9999);              // '9,999'   (one below general threshold)
   * SgEntityCountPipe.transform(12400);             // '12.4K'   (default kind = 'general')
   * SgEntityCountPipe.transform(-150000, 'clients');// '−150K'   (U+2212 minus)
   * SgEntityCountPipe.transform(null);              // '—'
   */
  static transform(
    value: number | null | undefined,
    kind: SgEntityCountKind = 'general',
    locale: string = 'en-US'
  ): string {
    if (value == null || !Number.isFinite(value)) {
      return '—';
    }

    const absValue = Math.abs(value);

    if (absValue < COUNT_THRESHOLDS[kind]) {
      return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value).replace(/-/g, '−');
    }

    const sign = value < 0 ? '−' : '';

    return `${sign}${abbreviateNumber(absValue, countDecimalsForUnit, locale)}`;
  }
}

// Spec §9 count tier: `≥100` in unit → 0dp, otherwise 1dp. Same shape as
// sgSmartCurrency compact mode; unit suffix is unused here.
function countDecimalsForUnit(unitValue: number, _unit: AbbreviationUnit): number {
  return unitValue >= 100 ? 0 : 1;
}
